import React, { useState } from "react";

// ── Shared form body for the seller profile editor.
// Used by both SellerProfileSetup (popup) and SellerProfilePage (full page).
// Renders the static field grid + the Resources & proof section.

export const SELLER_PROFILE_FIELDS = [
  { key: "seller_name", label: "Your Name", placeholder: "e.g. Alex Chen", half: true },
  { key: "seller_title", label: "Your Title", placeholder: "e.g. Account Executive", half: true },
  { key: "company_name", label: "Company Name", half: true },
  { key: "product_name", label: "Product Name", half: true },
  { key: "product_summary", label: "Product Summary", multiline: true, rows: 3 },
  { key: "what_it_does", label: "What It Does", multiline: true, rows: 3 },
  { key: "main_value_prop", label: "Main Value Prop", multiline: true, rows: 3 },
  { key: "key_use_cases", label: "Key Use Cases", multiline: true, rows: 3 },
  { key: "current_customers", label: "Current Customers", placeholder: "e.g. Notion, Linear, Vercel" },
  { key: "competitive_edge", label: "Competitive Edge", multiline: true, rows: 3 },
  { key: "goal_of_outreach", label: "Goal of Outreach", placeholder: "e.g. Book a 15-min discovery call" },
  { key: "preferred_messaging_tone", label: "Messaging Tone", placeholder: "e.g. Direct, warm, no buzzwords", half: true },
  { key: "sales_motion_style", label: "Sales Motion Style", placeholder: "e.g. PLG, top-down, bottom-up", half: true },
  {
    key: "key_signals_to_watch",
    label: "Key signals to watch (optional)",
    placeholder: "e.g. Hiring SREs, AWS→GCP migrations, recent CRO hires from competitors",
    multiline: true,
    rows: 3,
    hint: "What kinds of triggers tell you a prospect is a good fit RIGHT NOW? We use this to tailor per-prospect searches."
  }
];

export const buildEmptyProfile = () => ({
  name: "",
  seller_name: "",
  seller_title: "",
  company_name: "",
  product_name: "",
  product_summary: "",
  what_it_does: "",
  main_value_prop: "",
  key_use_cases: "",
  current_customers: "",
  competitive_edge: "",
  goal_of_outreach: "",
  preferred_messaging_tone: "",
  sales_motion_style: "",
  key_signals_to_watch: "",
  location: "",
  website: "",
  testimonials: [],
  customer_stories: [],
  use_cases: [],
  customers: [],
  segments: []
});

// Resource arrays default to [] for any profile loaded from BE that may not have them yet.
export const hydrateProfileForEdit = (existing) => ({
  testimonials: [],
  customer_stories: [],
  use_cases: [],
  customers: [],
  segments: [],
  ...(existing || {})
});

const LinkResourceList = ({ label, hint, items, onChange, urlPlaceholder, labelPlaceholder }) => {
  const safeItems = Array.isArray(items) ? items : [];

  const updateItem = (idx, key, value) => {
    onChange(safeItems.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const removeItem = (idx) => onChange(safeItems.filter((_, i) => i !== idx));
  const addItem = () => onChange([...safeItems, { url: "", label: "" }]);

  return (
    <div className="drResourceField">
      <div className="drResourceHeader">
        <label>{label}</label>
        {hint && <span className="drResourceHint">{hint}</span>}
      </div>
      {safeItems.length > 0 && (
        <div className="drResourceList">
          {safeItems.map((it, idx) => (
            <div className="drResourceRow" key={idx}>
              <input
                type="text"
                className="drResourceUrl"
                placeholder={urlPlaceholder}
                value={it.url || ""}
                onChange={(e) => updateItem(idx, "url", e.target.value)}
              />
              <input
                type="text"
                className="drResourceLabel"
                placeholder={labelPlaceholder}
                value={it.label || ""}
                onChange={(e) => updateItem(idx, "label", e.target.value)}
              />
              <button
                type="button"
                className="drResourceRemove"
                onClick={() => removeItem(idx)}
                title="Remove"
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" className="drResourceAdd" onClick={addItem}>
        + Add {label.toLowerCase()}
      </button>
    </div>
  );
};

const TagListInput = ({ label, hint, items, onChange, placeholder }) => {
  const safeItems = Array.isArray(items) ? items : [];
  const [draft, setDraft] = useState("");

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (safeItems.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...safeItems, trimmed]);
    setDraft("");
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Backspace" && !draft && safeItems.length > 0) {
      onChange(safeItems.slice(0, -1));
    }
  };

  const removeAt = (idx) => onChange(safeItems.filter((_, i) => i !== idx));

  return (
    <div className="drResourceField">
      <div className="drResourceHeader">
        <label>{label}</label>
        {hint && <span className="drResourceHint">{hint}</span>}
      </div>
      <div className="drTagInputWrap">
        {safeItems.map((tag, idx) => (
          <span className="drTag" key={idx}>
            {tag}
            <button
              type="button"
              className="drTagRemove"
              onClick={() => removeAt(idx)}
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className="drTagInput"
          placeholder={safeItems.length === 0 ? placeholder : ""}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={commitDraft}
        />
      </div>
    </div>
  );
};

const SellerProfileFormBody = ({ profile, setProfile }) => {
  // The profile NAME identifies this profile when a rep picks a seller profile
  // in a sequence. It defaults to the product name and mirrors it until the rep
  // renames it, after which we leave it alone. Existing profiles with no name
  // keep working (the picker falls back to company/product name), so this needs
  // no backend change and no migration.
  const [nameDirty, setNameDirty] = useState(
    !!(profile && profile.name && profile.name !== profile.product_name)
  );

  const updateField = (key, value) => {
    if (key === "product_name" && !nameDirty) {
      setProfile({ ...profile, product_name: value, name: value });
    } else {
      setProfile({ ...profile, [key]: value });
    }
  };

  const updateName = (value) => {
    setNameDirty(true);
    setProfile({ ...profile, name: value });
  };

  return (
    <>
      <div className="drInputGroup drInputGroupFull">
        <label>Seller profile name</label>
        <input
          type="text"
          value={profile.name || ""}
          onChange={(e) => updateName(e.target.value)}
          placeholder="e.g. Salesforce Sales Cloud"
        />
        <span className="drFieldHint">
          Defaults to your product name. Rename it to tell profiles apart when picking one in a sequence.
        </span>
      </div>

      <div className="drFieldGrid">
        {SELLER_PROFILE_FIELDS.map(({ key, label, multiline, rows = 3, placeholder, half, hint }) => (
          <div
            className={`drInputGroup ${half ? "drInputGroupHalf" : "drInputGroupFull"}`}
            key={key}
          >
            <label>{label}</label>
            {multiline ? (
              <textarea
                rows={rows}
                value={profile[key] || ""}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={placeholder || label}
              />
            ) : (
              <input
                type="text"
                value={profile[key] || ""}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={placeholder || label}
              />
            )}
            {hint && <span className="drFieldHint">{hint}</span>}
          </div>
        ))}
      </div>

      <div className="drResourcesSection">
        <div className="drResourcesHeader">
          <h4>Resources & proof</h4>
          <p>
            Drop links to testimonials, case studies, and use case docs we can
            reference when personalizing outreach. Customer and segment names
            help us spot relevant analogues during research.
          </p>
        </div>

        <LinkResourceList
          label="Testimonials"
          hint="Quote pages, video testimonials, G2/Capterra reviews"
          items={profile.testimonials}
          onChange={(v) => updateField("testimonials", v)}
          urlPlaceholder="https://example.com/testimonial"
          labelPlaceholder="What it shows (e.g. CFO at Acme on time savings)"
        />

        <LinkResourceList
          label="Customer stories"
          hint="Case studies, success stories, customer interviews"
          items={profile.customer_stories}
          onChange={(v) => updateField("customer_stories", v)}
          urlPlaceholder="https://example.com/case-study"
          labelPlaceholder="Customer + headline (e.g. Notion: 3× pipeline)"
        />

        <LinkResourceList
          label="Use case docs"
          hint="One-pagers, playbooks, solution briefs"
          items={profile.use_cases}
          onChange={(v) => updateField("use_cases", v)}
          urlPlaceholder="https://example.com/use-case.pdf"
          labelPlaceholder="What it covers (e.g. Outbound for series-A SaaS)"
        />

        <TagListInput
          label="Customer names"
          hint="Press Enter or comma to add"
          items={profile.customers}
          onChange={(v) => updateField("customers", v)}
          placeholder="e.g. Notion, Linear, Vercel"
        />

        <TagListInput
          label="Segments you sell into"
          hint="Press Enter or comma to add"
          items={profile.segments}
          onChange={(v) => updateField("segments", v)}
          placeholder="e.g. B2B SaaS, DevTools, mid-market"
        />
      </div>
    </>
  );
};

export default SellerProfileFormBody;
